/**
 * "12 playbooks are past review" — §7.2's digest, as a screen.
 *
 * The digest itself is the thing that changes behaviour; this is where someone
 * lands from it, and the two have to agree, so both read the same list from
 * `GET /api/documents/review/due` rather than one of them counting rows on its
 * own.
 *
 * Two scopes, because they are two different questions:
 *
 * - **`mine` is the nag, and the default.** §7.2 nags the owner, singular — a
 *   nag that reaches everyone who *could* fix a document is a nag nobody owns.
 * - **`all` is the compliance view.** It is still filtered through
 *   `permission_visible_nodes` (§9.2), so it is "everything past review that you
 *   can reach", never "everything past review".
 *
 * Nothing here offers to unpublish, hide, or archive a lapsed document. §7.2:
 * "Unpublishing stale documents is worse than stale documents." The only action
 * this list should ever grow is one that gets a document read.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useReviewDue } from '../../api/queries.ts';
import type { LapsedReview } from '../../api/types.ts';
import { Button } from '../../components/ui/button.tsx';
import { EmptyState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { formatCalendarDate, reviewPhrase } from './review.tsx';
import { DocumentStatusBadge, DocumentTypeBadge } from './vocabulary.tsx';

type Scope = 'mine' | 'all';

function headline(total: number, scope: Scope): string {
  if (total === 0)
    return scope === 'mine' ? 'Nothing of yours is past review' : 'Nothing is past review';
  const noun = total === 1 ? 'document is' : 'documents are';
  return scope === 'mine' ? `${total} of your ${noun} past review` : `${total} ${noun} past review`;
}

function ReviewRow({ document }: { document: LapsedReview }) {
  return (
    <TR>
      <TD>
        <Link
          to="/documents/$nodeId"
          params={{ nodeId: document.nodeId }}
          className="font-medium text-primary no-underline hover:underline"
        >
          {document.title}
        </Link>
      </TD>
      <TD>
        <DocumentTypeBadge type={document.documentType} />
      </TD>
      <TD>
        {/* The person §7.2 is nagging. Not a list of everyone who could fix it. */}
        {document.ownerName ? (
          <Link to="/admin/people/$personId" params={{ personId: document.ownerId }}>
            {document.ownerName}
          </Link>
        ) : (
          <span className="text-tertiary">Unknown</span>
        )}
      </TD>
      <TD>
        <span className="font-semibold text-warning-primary">
          {formatCalendarDate(document.reviewBy)}
        </span>
        <span className="block text-xs text-tertiary">{reviewPhrase(document.reviewBy)}</span>
      </TD>
      <TD>
        <DocumentStatusBadge status={document.status} />
      </TD>
      <TD>
        <Link to="/spaces/$spaceId" params={{ spaceId: document.spaceId }} className="text-xs">
          Space
        </Link>
      </TD>
    </TR>
  );
}

export function ReviewDuePage() {
  const [scope, setScope] = useState<Scope>('mine');
  const query = useReviewDue(scope);

  if (query.isError) {
    return (
      <PageError error={query.error} title="Past review" back={{ href: '/', label: 'Overview' }} />
    );
  }

  const total = query.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={query.isPending ? 'Past review' : headline(total, scope)}
        description={
          <>
            §7.2 calls the weekly past-review digest a bigger behaviour change than any feature in
            the spec. Everything listed here is still published and still readable — being past
            review flags a document, it never withdraws one.
          </>
        }
        actions={
          <div className="flex items-center gap-1 rounded-lg border border-primary p-1">
            <Button
              size="sm"
              variant={scope === 'mine' ? 'primary' : 'tertiary'}
              onPress={() => setScope('mine')}
            >
              Mine
            </Button>
            <Button
              size="sm"
              variant={scope === 'all' ? 'primary' : 'tertiary'}
              onPress={() => setScope('all')}
            >
              Everything I can see
            </Button>
          </div>
        }
      />

      <Card padded={false}>
        {query.isPending ? (
          <Loading />
        ) : total === 0 ? (
          <EmptyState
            title={scope === 'mine' ? 'Nothing of yours is past review' : 'Nothing is past review'}
            description={
              scope === 'mine'
                ? 'Either every playbook and reference you own has a date in the future, or none of them has a date at all — which is the failure mode §7.2 is about.'
                : 'Nothing you can reach is past its review date.'
            }
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Document</TH>
                <TH>Type</TH>
                <TH>Owner</TH>
                <TH>Was due</TH>
                <TH>Status</TH>
                <TH>Where</TH>
              </TR>
            </THead>
            <TBody>
              {query.data.documents.map((document) => (
                <ReviewRow key={document.nodeId} document={document} />
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
